import { itemFromtask } from '@features/task-picker/helpers'
import { useDebounce } from '@hooks/useDebounce'
import { useSearchTimeLoggerTasks } from '@hooks/useSearchTasks'
import { FC, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Item, SkeletonItem } from '../../components/Item'
import { Column } from './Column'
import { ActiveFilter } from './types'
import { getActiveFromFilter } from './utils/getActiveFromFilter'
import { getItemsAndTotalCount } from './utils/getItemsAndTotalCount'

interface Props {
  disabledTaskIds: Set<number>
  displaySearch?: boolean
  filter?: ActiveFilter
  heading?: string
  onActiveFilterChange: (filter: ActiveFilter) => void
  onSearchChange: (search: string) => void
  onSearchToggle: (displayed: boolean) => void
  onTaskClick: (task: Task, options: { selected: boolean }) => void
  projectId: number
  search?: string
  selectedTaskIds: Set<number>
}

export const TimeLoggerTaskColumn: FC<Props> = memo(
  ({
    disabledTaskIds,
    displaySearch,
    filter,
    heading,
    onActiveFilterChange,
    onSearchChange,
    onSearchToggle,
    onTaskClick,
    projectId,
    search = '',
    selectedTaskIds,
  }) => {
    const { t } = useTranslation()
    const debounced = useDebounce(search)

    const searchResult = useSearchTimeLoggerTasks({
      projectId,
      q: debounced,
      active: getActiveFromFilter(filter),
    })

    const { items, totalItems } = getItemsAndTotalCount(searchResult)

    return (
      <Column
        displaySearch={displaySearch}
        filter={filter}
        heading={heading}
        isFetching={searchResult.isFetchingNextPage || searchResult.isLoading}
        isInitiallyLoading={searchResult.isLoading}
        items={items}
        loadMoreItems={() => void searchResult.fetchNextPage()}
        activeFilterEnabled={true}
        onActiveFilterChange={onActiveFilterChange}
        onSearchChange={onSearchChange}
        onSearchToggle={onSearchToggle}
        renderItem={(task) => (
          <Item
            className="mx-9"
            key={`${task.id}-${task.name}`}
            item={itemFromtask(task, disabledTaskIds, selectedTaskIds, {
              hideActive:
                (filter?.active ?? false) !== (filter?.inactive ?? false),
            })}
            onItemClick={(_id, selected) => onTaskClick(task, { selected })}
          />
        )}
        searchValue={search}
        skeletonItem={<SkeletonItem />}
        subheading={t('features.timeLogging.taskSelection.tasks')}
        totalItems={totalItems}
      />
    )
  },
)

TimeLoggerTaskColumn.displayName = 'TimeLoggerTaskColumn'
